import { memo, useCallback, useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { format } from 'date-fns'
import { Summary } from 'lucide-react'
import { Button, Popover } from '@highlight/ui'
import type { CalendarEvent } from '@highlight/common'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import { getEffectiveCalendarColor, type Calendar, type CalendarColorValue } from '@renderer/hooks/use-calendars'
import { useViewActions, useViewOpenHandlers, type ViewOpenTarget } from '../../view-atoms'
import { useOpenBrief } from './brief/use-open-brief'
import { useMeetingBrief } from './brief/use-meeting-brief'
import { MEETING_VIEW_ID } from './meeting-view-id'
import { BriefPreview } from './brief/components/brief-preview'
import { MeetingRowContextMenuItems } from './meeting-row-actions'
import { MeetingPopover } from './meeting-popover'
import { useIsJoinWindowOpen } from './meeting-quick-actions'
import { useMeetingCallsToAction } from './use-meeting-calls-to-action'
import { RecordingMeetingRow } from './recording-meeting-row'
import { CellContextMenu } from '../cell-actions'
import { useTodoPopover } from '../todo-popover'
import { SIDEPANEL_CELL_SURFACE, SIDEPANEL_FILLED_HOVER_SURFACE, TODO_TITLE_TYPE } from '../cells/surface'
import { TodoRowSubtitle } from '../cells/todo-row-subtitle'
import type { Person } from '@renderer/chat/hooks/usePeople'
import { Participants } from '@renderer/overlay/components/participants'
import { listOtherAttendees, summarizeAttendees } from './attendee-summary'

const ROW_TRIGGER = 'sidepanel-meeting-row'

/** Furthest from the start, either side, that the countdown still shows. */
const COUNTDOWN_WINDOW_MINUTES = 60

/**
 * "Starts in 5 minutes" before the meeting, "Started 5 minutes ago" once it's
 * underway, and nothing more than an hour either side.
 */
function describeStartCountdown(msUntilStart: number): string | null {
  const minutes = Math.round(Math.abs(msUntilStart) / 60_000)
  if (minutes > COUNTDOWN_WINDOW_MINUTES) return null
  if (minutes === 0) return 'Starting now'
  const span = minutes === 1 ? '1 minute' : `${minutes} minutes`
  return msUntilStart > 0 ? `Starts in ${span}` : `Started ${span} ago`
}

/** Ticks on its own, so the memoized row around it doesn't re-render every minute. */
function StartCountdown({ start }: { start: Date }) {
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(interval)
  }, [])
  const text = describeStartCountdown(new Date(start).getTime() - now)
  if (!text) return null
  return <div className="text-foreground-secondary type-ui-xs font-normal">{text}</div>
}

/** "11:00am" */
function formatClockTime(date: Date): string {
  return format(date, 'h:mmaaa')
}

function calendarColorFor(calendarId: string | undefined, calendars: Array<Calendar> | undefined): string | undefined {
  const calendar = calendars?.find((c) => c.id === calendarId)
  if (!calendar) return undefined
  return getEffectiveCalendarColor(
    calendar.id,
    calendar.hexColor,
    calendar.metadata?.color as CalendarColorValue | undefined,
  )
}

interface MeetingPopoverRowProps {
  meeting: CalendarEvent
  /** Passed in by the list, so each row doesn't subscribe to the calendars itself. */
  calendars: Array<Calendar> | undefined
  sourceLabel?: string
  className?: string
  style?: CSSProperties
  /**
   * Hang the ready brief under the time line, tied to the row by a hairline
   * dropping from the icon. The Upcoming view turns this on: there every
   * meeting is a row, so a row is the only thing a brief can hang from (Figma
   * `7652:28726`). Home leaves it off and shows the brief in the popover.
   */
  showBriefPreview?: boolean
  /**
   * This is the meeting being transcribed. Passed in by the list, which already
   * resolves the recording's event, so idle rows hold no live-audio subscriptions.
   */
  isRecording?: boolean
  /**
   * `todo` lines the row up with the To-dos section's rows: a 16px icon, the
   * mark's size, the same 8px gap to the title, and the title's leading. Its
   * subtitle is the brief's headline, or failing that who the meeting is with.
   */
  appearance?: 'row' | 'todo'
  /** Adds a live "Starts in N minutes" / "Started N minutes ago" line under the title. */
  showCountdown?: boolean
  /** Names the attendees in a `todo` row's subtitle when there's no brief. Passed in for the same reason as `calendars`. */
  peopleByEmail?: ReadonlyMap<string, Person>
}

/**
 * The flat row Home's Upcoming and the Upcoming view use for every meeting:
 * conferencing-app icon, title, a one-line brief excerpt behind a calendar-colour
 * swatch, and start/end stacked on the right, with no container. A click opens
 * the meeting's popover.
 *
 * Deliberately not a variant of `MeetingCell`. The cell mounts the live-audio
 * feeds, the linked-notes query, and the join/transcribe machinery to decide
 * which CTA to show. A row shows none of that, and every meeting today renders
 * one, so paying for those subscriptions per row would put the whole section
 * back on the live-recording re-render path that `SidepanelUpcomingList`
 * narrows its own subscriptions to avoid. The one exception is the row being
 * recorded, which mounts the shared recording row.
 *
 * The one per-row fetch is the brief, for the excerpt: a shared, cached query.
 */
export const MeetingPopoverRow = memo(function MeetingPopoverRow({
  meeting,
  sourceLabel = 'Home',
  className,
  style,
  showBriefPreview = false,
  isRecording = false,
  appearance = 'row',
  showCountdown = false,
  calendars,
  peopleByEmail,
}: MeetingPopoverRowProps) {
  const { openView } = useViewActions()
  const openBrief = useOpenBrief()

  // The popover's Open drills into the Meeting View, never out to the calendar.
  // A ready brief routes through `openBrief` so the view opens on it. Presence
  // is `brief.conversationId` on the calendar GlobalState shell. Everything else
  // opens plain, deliberately not through `openBrief`, which treats an open as
  // consent to generate a brief.
  const hasBriefMeta = !!meeting.brief?.conversationId
  const hasBrief = hasBriefMeta || !!meeting.briefGenerating
  const { data: fetchedBrief } = useMeetingBrief(meeting.id, {
    enabled: hasBriefMeta,
    pollWhilePending: hasBriefMeta,
    presenceConversationId: meeting.brief?.conversationId,
  })
  const legacyInlineBrief = meeting.brief?.brief?.trim() ? meeting.brief : null
  const readyBrief = fetchedBrief ?? legacyInlineBrief
  const inlineBrief = showBriefPreview ? readyBrief : null
  const briefExcerpt = (fetchedBrief?.structure?.headline ?? readyBrief?.brief)?.trim()
  const openWith = useCallback(
    (open: ViewOpenTarget) => {
      if (hasBrief) {
        openBrief({ calendarEventId: meeting.id, title: meeting.title, meeting, sourceLabel, ...open })
        return
      }
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', calendarEventId: meeting.id, title: meeting.title, meeting },
        { sourceLabel, ...open },
      )
    },
    [hasBrief, meeting, openBrief, openView, sourceLabel],
  )
  const { onOpen: open, onPopout: openInWindow } = useViewOpenHandlers(openWith)

  const popover = useTodoPopover()
  const closePopover = popover.close
  const openFromPopover = useCallback(
    (event: MouseEvent) => {
      closePopover()
      open(event)
    },
    [closePopover, open],
  )
  // The thread slides up over the meeting, as a send from the Meeting View's own composer does.
  const openMeetingChat = useCallback(
    (conversationId: string) => {
      closePopover()
      openView(
        MEETING_VIEW_ID,
        {
          mode: 'note',
          calendarEventId: meeting.id,
          title: meeting.title,
          meeting,
          initialTab: 'brief',
          chatId: conversationId,
          chat: true,
          isFreshSubmit: true,
        },
        { sourceLabel },
      )
    },
    [closePopover, openView, meeting, sourceLabel],
  )

  const isJoinWindowOpen = useIsJoinWindowOpen(meeting)
  // A recording that overruns its slot is still the live focus, so it doesn't dim.
  const hasEnded = !isRecording && !!meeting.end && new Date(meeting.end).getTime() <= Date.now()
  const calendarColor = calendarColorFor(meeting.calendarId, calendars)
  const calendarSwatch = (
    <span
      aria-hidden="true"
      className="bg-foreground-placeholder size-1.5 shrink-0 rounded-[2px]"
      style={calendarColor ? { backgroundColor: calendarColor } : undefined}
    />
  )

  const isTodo = appearance === 'todo'
  const rowClassName = twMerge(
    'group/row relative flex w-full select-none flex-col gap-3 px-3',
    isTodo ? 'py-1.5' : 'py-2',
    SIDEPANEL_CELL_SURFACE,
    SIDEPANEL_FILLED_HOVER_SURFACE,
    className,
  )

  const otherAttendees = isTodo ? listOtherAttendees(meeting) : []
  const attendeeSummary = peopleByEmail ? summarizeAttendees(otherAttendees, peopleByEmail) : undefined
  // Three faces at most, so the badges' own "+N" never doubles the text's. A solo hold shows your own.
  const attendeeFaces =
    otherAttendees.length > 0 ? otherAttendees.slice(0, 3) : meeting.userEmail ? [meeting.userEmail] : []
  // Fixed to the widest time ("12:00pm"), so the times line up down the list.
  const times = (
    <div className="flex w-14 shrink-0 flex-col items-end gap-1 font-normal tabular-nums">
      <span className={twMerge('text-foreground type-ui-sm', isTodo && 'leading-normal')}>
        {formatClockTime(new Date(meeting.start))}
      </span>
      {meeting.end && (
        <span className="text-foreground-secondary type-ui-xs">{formatClockTime(new Date(meeting.end))}</span>
      )}
    </div>
  )
  const content = (
    <div className={twMerge('flex w-full flex-col gap-3', hasEnded && 'opacity-45')}>
      <div className={twMerge('flex w-full items-start', isTodo ? 'gap-2' : 'gap-3')}>
        <div className="flex shrink-0 flex-col items-center self-stretch">
          <span className={twMerge('flex h-5 items-center', isTodo && 'w-4 justify-center')}>
            <CalendarEventIconView event={meeting} size={isTodo ? 16 : 14} calendars={calendars} />
          </span>
          {/* The hairline drops out of the icon and runs alongside the text
                into the brief, so it only makes sense when a brief is there. The
                negative bottom margin carries it across the row's 12px gap so it
                lands on the brief's top edge rather than stopping short of it. */}
          {inlineBrief?.brief?.trim() && <span aria-hidden="true" className="bg-element-hover -mb-3 w-px flex-1" />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-1">
            <div className={twMerge('text-foreground type-ui-sm truncate font-normal', isTodo && TODO_TITLE_TYPE)}>
              {meeting.title}
            </div>
          </div>
          {showCountdown && <StartCountdown start={meeting.start} />}
          {isTodo ? (
            briefExcerpt ? (
              <TodoRowSubtitle icon={<Summary size={12} />} text={briefExcerpt} />
            ) : (
              <TodoRowSubtitle
                icon={
                  attendeeSummary &&
                  attendeeFaces.length > 0 && <Participants participants={attendeeFaces} hideTooltip size="xs" />
                }
                text={attendeeSummary}
              />
            )
          ) : (
            briefExcerpt && (
              <div className="text-foreground-secondary type-ui-xs flex min-w-0 items-center gap-1.5 font-normal">
                {calendarSwatch}
                <span className="truncate">{briefExcerpt}</span>
              </div>
            )
          )}
        </div>
        {isJoinWindowOpen && !isRecording ? <MeetingRowCallToAction meeting={meeting} fallback={times} /> : times}
      </div>
      {inlineBrief?.brief?.trim() && (
        // Full-bleed to the row's padding, flush with the icon's left edge, not
        // indented to the title's (Figma `7652:28726`), matching how Home's
        // MeetingCell hangs the same card.
        <BriefPreview
          brief={inlineBrief.brief}
          relevantLinks={inlineBrief.relevantLinks}
          showLinks={false}
          // No onClick of its own: the whole row is the popover trigger, and
          // a handler here would fire and then bubble into it.
          // Same surface colour as the row it sits in (per Figma). The
          // hairline edge and soft shadow are what separate it, not a fill.
          className="corner-smooth rounded-lg border-0 bg-transparent px-3 py-2 text-xs shadow-md"
        />
      )}
    </div>
  )

  return (
    <CellContextMenu items={<MeetingRowContextMenuItems meeting={meeting} onPopout={openInWindow} />}>
      {isRecording ? (
        <RecordingMeetingRow
          label={meeting.title}
          sourceLabel={sourceLabel}
          className={className}
          style={style}
          icon={<CalendarEventIconView event={meeting} size={14} calendars={calendars} />}
          startTime={meeting.start}
        />
      ) : (
        <Popover open={popover.open} onOpenChange={popover.onOpenChange}>
          <Popover.Trigger
            render={<div />}
            nativeButton={false}
            // A row to the panel's hotkeys, which step through cells.
            data-slot="cell"
            aria-label={meeting.title}
            className={rowClassName}
            style={style}
            onFocus={popover.onRowFocus}
            onBlur={popover.onRowBlur}
          >
            {content}
          </Popover.Trigger>
          <MeetingPopover
            meeting={meeting}
            calendars={calendars}
            onOpenMeeting={openFromPopover}
            onOpenMeetingChat={openMeetingChat}
            onClose={closePopover}
            open={popover.open}
            previewing={popover.previewing}
            swapping={popover.swapping}
            rowHostsCallToAction={isJoinWindowOpen}
          />
        </Popover>
      )}
    </CellContextMenu>
  )
})

/**
 * The meeting's black CTA in place of its times, when it has one. Only mounted
 * inside the join window, the one stretch a CTA can be black, so the rest of
 * the list never pays for the subscriptions behind it.
 */
function MeetingRowCallToAction({ meeting, fallback }: { meeting: CalendarEvent; fallback: ReactNode }) {
  const { primary } = useMeetingCallsToAction(meeting, ROW_TRIGGER)
  if (!primary?.isProminent) return <>{fallback}</>
  return (
    <Button
      size="sm"
      variant="primary"
      tone="inverse"
      loading={primary.isPending}
      // Top-aligned with the title. The bottom overhang keeps it from growing a single-line row.
      className="-mb-1 shrink-0"
      onClick={(event) => {
        // The row is the popover trigger.
        event.stopPropagation()
        primary.onSelect(event)
      }}
    >
      {primary.label}
    </Button>
  )
}
